import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Play, BarChart3, Zap, Briefcase, Award, Trophy, Settings, User,
  MessageSquare, Users, GraduationCap, Plug, FileText, Phone, Target,
  Mic, LayoutDashboard, BookOpen,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

const navigationItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', group: 'Navigate' },
  { label: 'Practice', icon: Play, path: '/dashboard?tab=practice', group: 'Navigate' },
  { label: 'Competitions', icon: Zap, path: '/dashboard?tab=competitions', group: 'Navigate' },
  { label: 'Jobs', icon: Briefcase, path: '/dashboard?tab=jobs', group: 'Navigate' },
  { label: 'Achievements', icon: Award, path: '/dashboard?tab=achievements', group: 'Navigate' },
  { label: 'Leaderboards', icon: Trophy, path: '/dashboard?tab=leaderboard', group: 'Navigate' },
  { label: 'Progress', icon: BarChart3, path: '/dashboard?tab=progress', group: 'Navigate' },
  { label: 'Training Plan', icon: BookOpen, path: '/dashboard?tab=tutorials', group: 'Navigate' },
  { label: 'Sessions History', icon: FileText, path: '/sessions', group: 'Navigate' },
  { label: 'Profile', icon: User, path: '/profile', group: 'Navigate' },
  { label: 'Settings', icon: Settings, path: '/settings', group: 'Navigate' },
  { label: 'Messages', icon: MessageSquare, path: '/messages', group: 'Navigate' },
  { label: 'Connections', icon: Users, path: '/connections', group: 'Navigate' },
  { label: 'Integrations', icon: Plug, path: '/integrations', group: 'Navigate' },
  { label: 'Teams', icon: GraduationCap, path: '/teams', group: 'Navigate' },
];

const actionItems = [
  { label: 'Speed Sell', icon: Mic, path: '/speed-sell', group: 'Quick Actions' },
  { label: 'Cold Call Practice', icon: Phone, path: '/cold-call', group: 'Quick Actions' },
  { label: 'AI Calling', icon: Target, path: '/ai-calling', group: 'Quick Actions' },
  { label: 'Grade a Recording', icon: FileText, path: '/grade-recording', group: 'Quick Actions' },
  { label: 'Resume Match', icon: FileText, path: '/resume-match', group: 'Quick Actions' },
];

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = useCallback((path: string) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  if (!user) return null;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Navigate">
          {navigationItems.map(item => (
            <CommandItem key={item.path} onSelect={() => runCommand(item.path)}>
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Quick Actions">
          {actionItems.map(item => (
            <CommandItem key={item.path} onSelect={() => runCommand(item.path)}>
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
